import React from 'react';
import type { RouteComponentProps, RouteProps } from 'react-router';
import { Redirect, useLocation } from 'react-router';
import { Route } from 'react-router-dom';

import * as routes from 'src/app/onboarding/routes';
import { useAuth } from 'src/context/AuthContext';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useQueryParam } from 'src/hooks/useQueryParam';
import { goLogout } from 'src/lib/goLogout';

import { Loading } from '../loading/Loading';

import { useIdleAction } from './useIdleAction';

/**
 * Redirect to the signup and onboarding flow, putting the current url in
 * local storage. When the user finishes either logging in and gets redirected
 * to the LoggedInRoute in RoutedApp ('/') or finishes signing up and hits the
 * LoggedInRoute, it will redirect them back to the
 * original requested url.
 */
export const LoggedInRoute = ({
  Component,
}: {
  Component: React.ComponentType;
}) => {
  const location = useLocation();
  const { isLoggedIn, user } = useAuth();
  const sessionError = useQueryParam('sessionError');
  const [redirectAfterLogin, setRedirectAfterLogin] =
    useLocalStorage('redirectAfterLogin');

  useIdleAction({
    timeMs: user?.logoutAfterIdleMs || null,
    stableCallback: goLogout,
  });

  if (!isLoggedIn) {
    const { pathname } = location;
    if (pathname === '/') {
      setRedirectAfterLogin(null);
    } else {
      setRedirectAfterLogin(location);
    }

    // TODO (jason) this is incredibly confusing. We should refactor
    // invite links to be more explicit about where they set the join
    // token so this is less hard to follow instead of relying on our
    // AuthenticatedRoutes to handle the redirect to the
    // AccountInvitationHandler after an account is created.
    // For invites and quick signup, go straight to signup
    if (/\/invite\//.test(pathname)) {
      return <Redirect to={routes.signup.location({ from: pathname })} />;
    }
    return (
      <Redirect to={routes.login.location({ from: pathname, sessionError })} />
    );
  }

  if (redirectAfterLogin && isLoggedIn) {
    setRedirectAfterLogin(null);
    return <Redirect to={redirectAfterLogin} />;
  }

  return <Component />;
};

/**
 * Unlike the LoggedOutRoute, this route component doens't introduce any
 * sideeffects, where a logged in user trying to access a public page ends up
 * getting redirected to the home page of the dashboard.
 */
type PublicRouteProps = {
  component?: (props: Record<string, unknown>) => React.ReactElement;
  render?: (props: RouteComponentProps) => React.ReactNode;
} & RouteProps;
export const PublicRoute = ({
  component: Component,
  render,
  ...rest
}: PublicRouteProps) => {
  if (!Component && !render) {
    throw new Error(
      'PublicRoute must be used with either a component or render function',
    );
  }

  const renderFn = (props: RouteComponentProps) =>
    Component ? <Component {...props} /> : render ? render(props) : null;

  return <Route {...rest} render={renderFn} />;
};

export type LoggedOutRouteProps = {
  component?: (props: Record<string, unknown>) => React.ReactElement;
  render?: (props: RouteComponentProps) => React.ReactNode;
} & RouteProps;

export const LoggedOutRoute = ({
  component: Component,
  render,
  ...rest
}: LoggedOutRouteProps) => {
  if (!Component && !render) {
    throw new Error(
      'LoggedOutRoute must be used with either a component or render function',
    );
  }
  const { user, isLoggedIn, loading } = useAuth();
  if (loading) return <Loading />;

  const renderFn = (props: RouteComponentProps) => {
    // If we are already logged in, go to the home page
    if (user && isLoggedIn) {
      const from = window.location.pathname;
      return (
        <Redirect
          to={{
            pathname: '/',
            state: { from },
          }}
        />
      );
    }
    return Component ? <Component {...props} /> : render ? render(props) : null;
  };

  return <Route {...rest} render={renderFn} />;
};
